import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import { dayMs } from "verdi-common/timeConstants";
import type { AxisSpec, StreamEntry } from "../ChartSpec";
import { BUCKET_WIDTH_MS, type Resolution } from "../resolution/pick-resolution";
import type { StreamPoint } from "../stream-cache/stream-cache-store";
import type { WindowedStreams } from "../stream-data/types";

/** Converts a raw database value to the display unit. Receives a unitCategory string (from the wire). */
export type GetConversion = (unitCategory: string) => (value: number) => number;

export type PreparedSeries = {
    streamId: number;
    color: string;
    name: string;
    /** Which sensor cluster on the device — the tiebreaker when two series share a device and a category. */
    sensorClusterName: string | null;
    /** Which channel on that cluster — the tiebreaker when two series share a cluster too. */
    sensorElementName: string | null;
    /** The axis this series is bound to — chart options and hover marks both address it by id. */
    axisId: string;
    /** What this series measures. The legend and the tooltip name it. */
    unitCategory: string;
    /** The category the axis is scaled in, which is the unit these values were converted to. */
    axisUnitCategory: string;
    seriesType: "line" | "bar";
    points: [Date, number | null][];
};

/**
 * A bucket is labelled by its start and echarts centres a bar on its x value, so an unshifted bar
 * covers half the bucket before the interval it sums. Half a width forward makes it span its own.
 *
 * Lines keep the bucket start — a vertex there is the usual convention. Bars never reach the raw
 * tier in the app (a bar would need a window of seconds), but debugForceRawStreams can get
 * there, and a raw point has no interval to centre on.
 */
function barTimeShiftMs(stream: StreamEntry, resolution: Resolution): number {
    if (stream.seriesType !== "bar" || resolution === "raw") return 0;
    return BUCKET_WIDTH_MS[resolution] / 2;
}

/**
 * Echarts sizes every bar from the smallest gap between points in the series, so empty buckets stay
 * in the data — drop them and a gauge that reports sparsely gets bars wider than the bucket they
 * sum. A null draws no bar, so nothing marks the empty bucket.
 */
function barPoints(
    points: StreamPoint[],
    shiftMs: number,
    convert: (value: number) => number,
): [Date, number | null][] {
    return points.map((point) => [new Date(point.t + shiftMs), point.avg === null ? null : convert(point.avg)]);
}

/** How long a reading stays current before the line breaks and the tooltip stops showing it. */
export function staleAfterMsFor(unitCategory: string): number {
    return getUnitCategoryConfiguration(unitCategory)?.staleAfterMs ?? dayMs;
}

function linePoints(
    points: StreamPoint[],
    convert: (value: number) => number,
    staleAfterMs: number,
): [Date, number | null][] {
    const nullInsertedPoints: [Date, number | null][] = [];

    points
        // Empty buckets arrive as nulls; drop them so the time delta below is calculated between real readings
        .filter((point): point is StreamPoint & { avg: number } => point.avg !== null)
        .forEach((point, index, array) => {
            nullInsertedPoints.push([new Date(point.t), convert(point.avg)]);

            const nextPoint = array[index + 1];

            // With ECharts connectNulls: false, inserting our own null value breaks the line when
            // we've detected a stale period
            if (nextPoint && nextPoint.t - point.t > staleAfterMs) {
                nullInsertedPoints.push([new Date(point.t + staleAfterMs), null]);
            }
        });

    return nullInsertedPoints;
}

// Bring together the spec and the stream data to produce ECharts series
export function prepareSeries(
    dataByStreamId: WindowedStreams,
    streams: StreamEntry[],
    axes: AxisSpec[],
    getConversion: GetConversion,
): PreparedSeries[] {
    if (!dataByStreamId || Object.keys(dataByStreamId).length === 0 || !streams || streams.length === 0) {
        return [];
    }

    return streams
        .filter((stream) => dataByStreamId[stream.streamId])
        .map((stream) => {
            const data = dataByStreamId[stream.streamId];
            const axis = axes.find((a) => a.id === stream.axisId);

            // Axes are built from these streams, so a matching axis always exists.
            if (!axis) {
                throw new Error(`No axis found for stream ${stream.streamId} (axisId ${stream.axisId})`);
            }

            const convert = getConversion(axis.unitCategory);

            const staleAfterMs = staleAfterMsFor(axis.unitCategory);

            const points =
                stream.seriesType === "bar"
                    ? barPoints(data.points, barTimeShiftMs(stream, data.resolution), convert)
                    : linePoints(data.points, convert, staleAfterMs);

            return {
                color: stream.color,
                streamId: stream.streamId,
                name: stream.name,
                sensorClusterName: stream.sensorClusterName ?? null,
                sensorElementName: stream.sensorElementName ?? null,
                axisId: axis.id,
                unitCategory: stream.unitCategory,
                axisUnitCategory: axis.unitCategory,
                seriesType: stream.seriesType,
                points,
            };
        });
}
